import React, { useState, useEffect } from "react";
import { ResizableBox } from "react-resizable";
import * as Icon from "react-feather";
import { Form, Accordion } from "react-bootstrap";
import { openGitHubPullRequestService, getRepoConfigService } from "../services/editor";
import ByPassTestsModal from "./Modals/ByPassTests";
import { CircularProgress, Button } from "@mui/material"
import DbtProfileDropdown from "./ScheduleModal/DbtProfileDropdown";

export default function PullRequestModal({
  setIsShown,
  selectedBranch,
  dbtCommand,
  activeTenant,
  test_dbt_model,
  dbt_test_status,
  dbt_logs,
  dbt_url,
  dbtProfile,
  dbtProfiles,
  setDbtProfile
}) {
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [dbtRepoName, setDbtRepoName] = useState(null);

  // Fetch repository configuration on mount
  useEffect(() => {
    const fetchRepoConfig = async () => {
      try {
        const response = await getRepoConfigService();
        if (response?.data?.dbt_repo) {
          setDbtRepoName(response.data.dbt_repo);
        } else {
          // Fallback to tenant name if API fails
          const tenantName = typeof activeTenant === 'string'
            ? activeTenant
            : activeTenant.name || 'default';
          setDbtRepoName(tenantName);
        }
      } catch (error) {
        console.error('Error fetching repo config:', error);
        const tenantName = typeof activeTenant === 'string'
          ? activeTenant
          : activeTenant.name || 'default';
        setDbtRepoName(tenantName);
      }
    };
    fetchRepoConfig();
  }, [activeTenant]);

  const handleCreatePullRequest = () => {
    if (dbt_test_status === "failed" || dbt_test_status === "testing" || (!dbt_test_status && dbt_logs)) {
      setShowBypassModal(true);
    } else {
      openGitHubPullRequest();
    }
  };

  const openGitHubPullRequest = async () => {
    if (dbt_test_status === "testing") await openGitHubPullRequestService(dbt_url);

    // Use dbtRepoName if available, otherwise fallback to tenantName
    const repoName = dbtRepoName || (typeof activeTenant === 'string' ? activeTenant : activeTenant.name || 'default');
    const pullRequestUrl = `https://github.com/datajolt/${repoName}/compare/main...${selectedBranch}`;
    window.open(pullRequestUrl, "_blank");
  };    

  const handleTestClick = () => {
    test_dbt_model(dbtCommand);
  };

  const getStatusIcon = (isButton = false) => {
    if (dbt_test_status === "testing" || (!dbt_test_status && dbt_logs)) {
      return (
        <CircularProgress size={16} color="#fff" />
      );
    } else if (dbt_test_status === "success") {
      return <Icon.Check color={isButton ? "white" : "green"} />;
    } else if (dbt_test_status === "failed") {
      return <Icon.X color={isButton ? "white" : "red"} />;
    }
    return null;
  };

  return (
    <>
      <ResizableBox
        className={`dragged-box react-resizable sidebar-modal-wrapper pull-request-modal`}
        height={window.innerHeight - 50}
        width={window.innerWidth / 3}
        maxConstraints={[window.innerWidth - 65, window.innerHeight - 50]}
        minConstraints={[400, 400]}
        resizeHandles={["w"]}
        id="active-modal-sidebar"
        style={{
          zIndex: 1050, // Higher z-index to ensure it appears above Robin ChatSidebar
          position: 'relative',
          boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)'
        }}
      >  
        <section className="schedule-sidebar-content" style={{ gap: "11px" }}>
          <h2 style={{ marginBottom: '12px', position: 'relative' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '10px',
                borderRadius: '10px',
                background: 'rgba(85, 110, 230, 0.08)',
                color: '#556ee6',
                fontWeight: '600',
                fontSize: '13px',
                letterSpacing: '0.2px',
                boxShadow: '0 2px 6px rgba(85, 110, 230, 0.1)',
                border: '2px solid rgba(19, 25, 59, 0.15)',
                position: 'relative',
                paddingRight: '45px',
                width: '100%'
              }}
            >            
              <Icon.ChevronsRight
                color="#556ee6"
                onClick={() => setIsShown(false)}
                role="button"
                style={{ marginRight: '8px' }}
                size={16}
              />{" "}
              Pull Request for {selectedBranch}
              <Icon.GitPullRequest
                color="#556ee6"
                size={18}
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              />
            </span>
          </h2>
          <Form
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Form.Group controlId="dependencies">
            <Form.Label 
                    style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#495057',
                    }}
                  >
              Testing your branch with the following command</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control
                  value={dbtCommand}
                  type="text"
                  style={{fontSize: "11px"}}
                  disabled
                />
                <DbtProfileDropdown
                  dbtProfile={dbtProfile}
                  dbtProfiles={dbtProfiles}
                  setDbtProfile={setDbtProfile}
                  className="test-button p-0 border border-white"
                  sx={{
                    height: "34px",
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleTestClick}
                  disabled={dbt_test_status === "testing" || (!dbt_test_status && dbt_logs)}
                  sx={{
                    height: "32px",
                    backgroundColor: "#3F80EA",
                    color: "#fff",
                    fontSize: "11px",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',

                    "&:disabled": {
                      backgroundColor: "#3F80EA",
                      color: "#fff"
                    }
                  }}
                >
                  <Icon.Code width={28} />&nbsp;
                  {dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? "Testing..." : "Test"}
                </Button>
              </div>
            </Form.Group>
            <br />
            <Accordion
              defaultActiveKey="0"
              className="pl-0"
              style={{ flex: 1 }}
              activeKey="0"
            >
              <Accordion.Item
                eventKey="0"
                className="bg-white"
                style={{
                  borderRadius: '18px',
                  background: 'linear-gradient(to bottom, #fff, #f9fbfe)',
                  boxShadow: '0 8px 20px rgba(85, 110, 230, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  border: 'none',
                  transition: 'all 0.05s ease-in-out',
                  height: "100%"
                }}
              >
           <Accordion.Header
                style={{
                  background: 'transparent',
                  border: 'none',
                  width: '100%',
                  borderRadius: '18px 18px 0 0',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  className="d-flex align-items-center w-100" 
                  style={{ 
                    fontWeight: '600', 
                    fontSize: '12px', 
                    color: '#556ee6',
                    letterSpacing: '0.2px'
                  }}
                >
                  {/* Conditional icon container based on status */}
                  {getStatusIcon() === "success" ? (
                    <div style={{
                      backgroundColor: 'rgba(46, 202, 106, 0.15)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 5px rgba(46, 202, 106, 0.2)'
                    }}>
                      <Icon.Check color="#2eca6a" size={16} />
                    </div>
                  ) : getStatusIcon() === "failed" ? (
                    <div style={{
                      backgroundColor: 'rgba(241, 85, 85, 0.15)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 5px rgba(241, 85, 85, 0.2)'
                    }}>
                      <Icon.X color="#f15555" size={16} />
                    </div>
                  ) : dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? (
                    <div style={{
                      backgroundColor: 'rgba(85, 110, 230, 0.1)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      transition: 'all 0.2s ease'
                    }}>
                      <CircularProgress 
                        size={18} 
                        style={{ 
                          color: '#556ee6',
                          animation: 'rotate 1.5s linear infinite'
                        }} 
                      />
                    </div>
                  ) : (
                    <div 
                      style={{
                        backgroundColor: 'rgba(85, 110, 230, 0.1)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}
                    >
                      <Icon.Code size={16} color="#556ee6" />
                    </div>
                  )}
                  
                  {dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? (
                    <span 
                      style={{
                        animation: 'pulseText 1.5s infinite ease-in-out',
                        display: 'inline-flex',
                        alignItems: 'center',
                        minWidth: '180px' // Prevent layout shifts
                      }}
                    >
                      Testing your branch...
                      <span 
                        style={{
                          width: '18px',
                          textAlign: 'left',
                          display: 'inline-block',
                          animation: 'ellipsis 1.5s infinite steps(4, end)',
                          overflow: 'hidden'
                        }}
                      >
                        ...
                      </span>
                    </span>
                  ) : (
                    "DBT Test Logs"
                  )}
                </div>
              </Accordion.Header>
                {dbt_logs && (
                  <Accordion.Body className="p-0 m-2 h-100">
                    <div
                      className="log-display"
                      dangerouslySetInnerHTML={{
                        __html: dbt_logs,
                      }}
                    />
                  </Accordion.Body>
                )}
              </Accordion.Item>
            </Accordion>
          </Form>

          <Button
            variant="contained"
            onClick={handleCreatePullRequest}
            sx={{
              backgroundColor: dbt_test_status === "failed" || dbt_test_status === "not_started" || dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? "#dc3545" : "#54cb75",
              padding: "12px 16px",
              "&:hover": {
                opacity: 0.8
              }
            }}
          >
          <span style={{ fontWeight: "800", letterSpacing: "0.3px", fontSize: "12px" }}>
            {dbt_test_status === "failed" || dbt_test_status === "not_started" || dbt_test_status === "testing" || (!dbt_test_status && dbt_logs) ? (
              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                width: "100%",
                position: "relative",
                paddingRight: "35px"
              }}>
                <span>Bypass test & Create pull request</span>
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  padding: "5px",
                  height: "24px",
                  width: "24px",
                  position: "absolute",
                  right: "5px"
                }}>
                  <Icon.GitPullRequest width={16} />
                </div>
              </div>
            ) : (
              <>Create Pull Request <Icon.GitPullRequest width={24} className="button-icon" /></>
            )}
            </span>
          </Button>
        </section>
      </ResizableBox>

      <ByPassTestsModal
        isShown={showBypassModal}
        onHide={() => setShowBypassModal(false)}
        onAction={() => {
          setShowBypassModal(false);
          openGitHubPullRequest();
        }}
      />
    </>
  );
}
